import { useSelector } from "react-redux";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MapPin } from "react-feather";
import Input from "../../input/Input";
import SearchDropdown from "../../input/SearchDropdown";
import { getOtherRefSearchDropdown } from "../../../api/keyvalue";
import Button from "../../input/Button";
import MetabaseDashboard from "./MetabaseDashboard";

const initState = {
  from: null,
  to: null,
  customer_seq: [],
  plant: [],
};

const CommonMetabaseDashboard = ({ url }) => {
  const userData = useSelector((state) => state.user.userDetails);
  const user = useSelector((state) => state.user);

  const [searched, setSearched] = useState(false);
  const [parameters, setParameters] = useState(initState);
  const [accordionActive, setAccordionActive] = useState(true);
  const [reloadDates, setReloadDates] = useState(true);
  const [resetSearchFields, setResetSearchFields] = useState(false);

  const [errors, setErrors] = useState({
    from: null,
    to: null,
    customerSeq: null,
  });

  useEffect(() => {
    if (reloadDates) {
      const aWeekAgo = new Date();
      aWeekAgo.setDate(aWeekAgo.getDate() - 7);
      const formattedWeekAgo = aWeekAgo.toISOString().split("T")[0];
      const formattedToday = new Date().toISOString().split("T")[0];
      setParameters((prevState) => ({
        ...prevState,
        from: formattedWeekAgo,
        to: formattedToday,
      }));
    }
    setReloadDates(false);
  }, [reloadDates]);

  const validateSearch = useCallback(() => {
    let hasErrors = false;
    if (!parameters.from) {
      setErrors((prevState) => ({
        ...prevState,
        from: "From Date is Mandatory",
      }));
      hasErrors = true;
    }
    if (!parameters.to) {
      setErrors((prevState) => ({
        ...prevState,
        to: "To Date is Mandatory",
      }));
      hasErrors = true;
    }
    if (
      parameters.customer_seq === undefined ||
      parameters.customer_seq === null ||
      parameters.customer_seq?.length <= 0
    ) {
      setErrors((prevState) => ({
        ...prevState,
        customerSeq: "Customer is Mandatory",
      }));
      hasErrors = true;
    }

    return hasErrors;
  }, [parameters.customer_seq, parameters.to, parameters.from]);

  const hasErrors = useCallback(() => {
    if (!parameters.from) {
      return true;
    }
    if (!parameters.to) {
      return true;
    }
    if (
      parameters.customer_seq === undefined ||
      parameters.customer_seq === null ||
      parameters.customer_seq?.length <= 0
    ) {
      return true;
    }

    return false;
  }, [parameters.customer_seq, parameters.to, parameters.from]);

  useEffect(() => {
    const hasParamErrors = hasErrors();
    if (searched && hasParamErrors) {
      setSearched(false);
    }
  }, [hasErrors, searched]);

  const searchHandler = () => {
    const hasErrors = validateSearch();
    if (hasErrors) {
      toast.warn("Please fill all the mandatory fields", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    }
    setSearched(true);
    setAccordionActive(false);
  };

  const resetHandler = () => {
    setParameters(initState);
    setReloadDates(true);
    setResetSearchFields(true);
    setSearched(false);
    setAccordionActive(true);
  };

  return (
    <>
      <div
        className="ui styled accordion"
        style={{ width: "100%", marginTop: "15px", marginBottom: "15px" }}
      >
        <div
          className={`title ${accordionActive ? "active" : ""}`}
          onClick={() => setAccordionActive((prevState) => !prevState)}
        >
          <div>
            Search Filters
            <MapPin size={14} style={{ marginLeft: "10px" }} />
            <i className="dropdown icon"></i>
          </div>
        </div>
        <div className={`content ${accordionActive ? "active" : ""}`}>
          <div className="ui form">
            <div className="four fields">
              <Input
                required={true}
                type="date"
                label="From Date"
                name="fromDate"
                error={errors.from}
                resetError={() =>
                  setErrors((prevState) => {
                    const { from: _, ...rest } = prevState;
                    return rest;
                  })
                }
                max={parameters?.to ?? null}
                value={parameters?.from}
                onChange={(e) => {
                  setParameters((prevState) => ({
                    ...prevState,
                    from: e.target.value,
                  }));
                }}
              />
              <Input
                required={true}
                label="To Date"
                type="date"
                name="toDate"
                min={parameters?.from}
                value={parameters?.to}
                disabled={!parameters?.from}
                error={errors.to}
                resetError={() =>
                  setErrors((prevState) => {
                    const { to: _, ...rest } = prevState;
                    return rest;
                  })
                }
                onChange={(e) => {
                  setParameters((prevState) => ({
                    ...prevState,
                    to: e.target.value,
                  }));
                }}
              />
              {userData.type === 1 ? (
                <SearchDropdown
                  key={1}
                  required={true}
                  editMode={false}
                  reset={resetSearchFields}
                  setReset={setResetSearchFields}
                  error={errors.customerSeq}
                  resetError={() =>
                    setErrors((prevState) => {
                      const { customerSeq: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  occupySpace={false}
                  multiple={true}
                  multiValueRemoveHandler={(key) => {
                    setParameters((prevState) => ({
                      ...prevState,
                      customer_seq: prevState.customer_seq.filter(
                        (item) => item !== parseInt(key),
                      ),
                    }));
                  }}
                  onResultSelect={(data) =>
                    setParameters((prevState) => ({
                      ...prevState,
                      customer_seq: [...prevState.customer_seq, data],
                    }))
                  }
                  api={getOtherRefSearchDropdown}
                  apiDependency={{
                    type: "stakeholder_type",
                    value: "Customer",
                  }}
                  label="Customer"
                />
              ) : (
                <SearchDropdown
                  key={1}
                  required={true}
                  editMode={false}
                  reset={resetSearchFields}
                  setReset={setResetSearchFields}
                  error={errors.customerSeq}
                  resetError={() =>
                    setErrors((prevState) => {
                      const { customerSeq: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  occupySpace={false}
                  multiple={true}
                  multiValueRemoveHandler={(key) => {
                    setParameters((prevState) => ({
                      ...prevState,
                      customer_seq: prevState.customer_seq.filter(
                        (item) => item !== parseInt(key),
                      ),
                    }));
                  }}
                  onResultSelect={(data) =>
                    setParameters((prevState) => ({
                      ...prevState,
                      customer_seq: [...prevState.customer_seq, data],
                    }))
                  }
                  api={getOtherRefSearchDropdown}
                  apiDependency={{
                    type: "user_stakeholder",
                    value: "Requesting Customer",
                    param2: user.username,
                  }}
                  label="Customer"
                />
              )}
              <SearchDropdown
                key={2}
                editMode={false}
                reset={resetSearchFields}
                disabled={
                  !parameters?.customer_seq ||
                  parameters?.customer_seq.length === 0
                }
                setReset={setResetSearchFields}
                occupySpace={false}
                multiple={true}
                multiValueRemoveHandler={(key) => {
                  setParameters((prevState) => ({
                    ...prevState,
                    plant: prevState.plant.filter(
                      (item) => item !== parseInt(key),
                    ),
                  }));
                }}
                onResultSelect={(data) =>
                  setParameters((prevState) => ({
                    ...prevState,
                    plant: [...prevState.plant, data],
                  }))
                }
                api={getOtherRefSearchDropdown}
                apiDependency={
                  userData.type === 1
                    ? {
                        type: "stakeholder_type",
                        value: "Shipper",
                        param2:
                          parameters?.customer_seq?.length > 0
                            ? parameters?.customer_seq.join(",")
                            : "",
                      }
                    : {
                        type: "user_stakeholder",
                        value: "Shipper",
                        param1:
                          parameters?.customer_seq?.length > 0
                            ? parameters?.customer_seq.join(",")
                            : "",
                        param2: user.username,
                      }
                }
                label="Shipper/Plant"
              />
            </div>
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <div className="field" style={{ width: "100%" }}>
                <Button template="search" onClick={searchHandler} />
                <Button template="searchReset" onClick={resetHandler} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <MetabaseDashboard
        url={url}
        parameters={{
          ...parameters,
          customer_seq:
            parameters?.customer_seq != null &&
            parameters?.customer_seq.length > 0
              ? parameters?.customer_seq?.join(",")
              : "",
          plant:
            parameters?.plant != null && parameters?.plant.length > 0
              ? parameters?.plant?.join(",")
              : "-1",
        }}
        hideList={["from", "to", "customer_seq", "plant"]}
        showDashboard={searched}
      />
    </>
  );
};

export default CommonMetabaseDashboard;
